import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Loader2, Send } from 'lucide-react'
import axios from 'axios'
import { toast } from '@/components/ui/sonner'

const API_BASE = import.meta.env.VITE_API_URL || ''

interface WorkWithMeModalProps {
  isOpen: boolean
  onClose: () => void
  username: string
  athleteName: string
}

export default function WorkWithMeModal({
  isOpen,
  onClose,
  username,
  athleteName,
}: WorkWithMeModalProps) {
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: '',
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.id]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields')
      return
    }

    setLoading(true)
    try {
      const res = await axios.post(`${API_BASE}/api/public/p/${username}/inquire`, formData)
      if (res.data.success) {
        toast.success('Inquiry sent successfully!')
        onClose()
        setFormData({ name: '', company: '', email: '', message: '' })
      } else {
        toast.error(res.data.error || 'Failed to send inquiry')
      }
    } catch (error: any) {
      console.error('Inquiry error:', error)
      toast.error(error.response?.data?.error || 'Failed to send inquiry')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#1A2530] border-white/10 text-white sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold font-outfit">
            Work with {athleteName}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-gray-300">
              Name <span className="text-red-400">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your name"
              className="bg-white/5 border-white/10 focus:border-[#C0FD72]/50 text-white"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="company" className="text-gray-300">
              Company (Optional)
            </Label>
            <Input
              id="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Company name"
              className="bg-white/5 border-white/10 focus:border-[#C0FD72]/50 text-white"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-300">
              Email <span className="text-red-400">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              className="bg-white/5 border-white/10 focus:border-[#C0FD72]/50 text-white"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-gray-300">
              Message <span className="text-red-400">*</span>
            </Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={handleChange}
              placeholder={`Hi ${athleteName}, I'd like to work with you on...`}
              className="bg-white/5 border-white/10 focus:border-[#C0FD72]/50 text-white min-h-[120px]"
            />
          </div>

          <div className="pt-2">
            <Button
              type="submit"
              disabled={loading}
              variant="lime"
              className="w-full font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  Send Inquiry
                  <Send className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
